/**
 * Playground: free-choice drawings grouped by category and level, with seasonal tags, a few free
 * preview items (first-time stage) and the rest premium.
 */
import { t } from '../text';
import { buildPlaygroundMiniUnit } from './lessons';
import type { Drawing, MiniUnit, PlaygroundCategory, PlaygroundConfig, PlaygroundItem, SubjectCategory } from './model';

export const playgroundItemId = (drawingId: string) => `pg-${drawingId}`;

export const PLAYGROUND_CATEGORIES: PlaygroundCategory[] = [
  { id: 'quick', title: t('Quick doodles', 'Vẽ nhanh'), icon: 'bolt' },
  { id: 'animals', title: t('Animals', 'Động vật'), icon: 'paw' },
  { id: 'nature', title: t('Nature', 'Thiên nhiên'), icon: 'leaf' },
  { id: 'characters', title: t('Characters', 'Nhân vật'), icon: 'smile' },
  { id: 'manga', title: t('Manga style', 'Truyện tranh'), icon: 'sparkle' },
  { id: 'cute', title: t('Cute objects', 'Đồ dễ thương'), icon: 'cupcake' },
];

const CATEGORY_FOR: Record<SubjectCategory, string> = {
  animals: 'animals',
  nature: 'nature',
  characters: 'characters',
  anime: 'manga',
  cuteObjects: 'cute',
  food: 'cute',
  shapes: 'quick',
  vehicles: 'quick',
  fantasy: 'manga',
};

const SEASONS: Record<string, PlaygroundItem['season']> = {
  'doodle-ghost': 'autumn',
  'doodle-pumpkin': 'autumn',
  'doodle-snowman': 'winter',
  'doodle-flower': 'spring',
  'nature-beach-sunset': 'summer',
  'cute-gift-box': 'holiday',
};

/** Free in the first-time stage (and for free users afterwards). */
export const PREVIEW_DRAWINGS = ['doodle-star', 'doodle-heart', 'doodle-cloud', 'doodle-snail', 'animals-cat', 'cute-donut'];

export function buildPlayground(doodles: Drawing[], lessons: Drawing[], startN = 0): { playground: PlaygroundConfig; miniUnits: MiniUnit[] } {
  const items: PlaygroundItem[] = [];
  const miniUnits: MiniUnit[] = [];
  let n = startN;
  for (const d of [...doodles, ...lessons]) {
    const isDoodle = doodles.includes(d);
    const mu = buildPlaygroundMiniUnit(d, n++);
    miniUnits.push(mu);
    const season = SEASONS[d.id];
    items.push({
      id: playgroundItemId(d.id),
      categoryId: isDoodle ? 'quick' : CATEGORY_FOR[d.category],
      miniUnitId: mu.id,
      drawingId: d.id,
      level: isDoodle ? 1 : (Math.min(3, d.difficulty) as 1 | 2 | 3),
      tags: [d.category, d.subject, ...d.skills],
      ...(season ? { season } : {}),
      ...(PREVIEW_DRAWINGS.includes(d.id) ? { free: true } : {}),
    });
  }
  return {
    playground: { categories: PLAYGROUND_CATEGORIES, items, previewItemIds: PREVIEW_DRAWINGS.map(playgroundItemId).filter((id) => items.some((i) => i.id === id)) },
    miniUnits,
  };
}

/** "My picks": items matching the profile's favourite subjects, not yet completed. */
export function myPicks(cfg: PlaygroundConfig, drawings: Map<string, Drawing>, favorites: string[], completed: string[], n = 6): PlaygroundItem[] {
  const fav = new Set(favorites);
  return cfg.items.filter((i) => !completed.includes(i.id) && fav.has(drawings.get(i.drawingId)?.category ?? '')).slice(0, n);
}

/** Recommendations: next items sharing skills with what was drawn most recently. */
export function recommendations(cfg: PlaygroundConfig, completed: string[], n = 6): PlaygroundItem[] {
  const last = cfg.items.find((i) => i.id === completed[completed.length - 1]);
  if (!last) return cfg.items.filter((i) => i.level === 1 && !completed.includes(i.id)).slice(0, n);
  const score = (i: PlaygroundItem) => i.tags.filter((tg) => last.tags.includes(tg)).length;
  return cfg.items
    .filter((i) => i.id !== last.id && !completed.includes(i.id))
    .sort((a, b) => score(b) - score(a))
    .slice(0, n);
}

/** Current season for seasonal promotion (northern-hemisphere months). */
export function currentSeason(now: Date): NonNullable<PlaygroundItem['season']> {
  const m = now.getMonth();
  if (m === 11) return 'holiday';
  if (m <= 1) return 'winter';
  if (m <= 4) return 'spring';
  if (m <= 7) return 'summer';
  return 'autumn';
}

/* ------------------------------ "All" = curated discovery ------------------------------ */

/** Max cards on one discovery shelf; the rest open from its "See more (N)" card. */
export const DISCOVERY_SHELF_MAX = 8;

export interface DiscoveryInput {
  /** Items this child can see at all (audience / flags already applied). */
  items: PlaygroundItem[];
  drawings: Map<string, Drawing>;
  /** Completed playground item ids (order = when). */
  completed: string[];
  /** Mini units with a saved resume point (started, not finished). */
  started: string[];
  /** Favourite subjects from onboarding (drawing categories). */
  favorites: string[];
  /** Playable right now (access, level, crossover / picture rules). */
  isOpen: (i: PlaygroundItem) => boolean;
  /** Highest playground level open to this child. */
  openLevel: number;
  /** Items already shown elsewhere on the page (today's pick). */
  exclude?: string[];
}

export interface Discovery {
  /** Started, not finished — most recent first. */
  continueItems: PlaygroundItem[];
  /** Recently unlocked: the newest open level, not started yet. */
  fresh: PlaygroundItem[];
  /** Level-appropriate items scored by favourite subjects + what was drawn last. */
  forYou: PlaygroundItem[];
  /** Two starters from every category the child has never drawn from. */
  notTried: PlaygroundItem[];
  /** Pool for "Surprise me!": open and not drawn yet (or anything open when all are done). */
  surprise: PlaygroundItem[];
}

export function playgroundDiscovery(input: DiscoveryInput): Discovery {
  const { items, drawings, completed, favorites, isOpen, openLevel } = input;
  const done = new Set(completed);
  const ex = new Set(input.exclude ?? []);
  const startedMu = new Set(input.started);
  const fav = new Set(favorites);
  const byMu = new Map(items.map((i) => [i.miniUnitId, i] as const));
  const continueItems = [...input.started]
    .reverse()
    .map((mu) => byMu.get(mu))
    .filter((i): i is PlaygroundItem => !!i && !done.has(i.id) && isOpen(i));
  const fresh = openLevel > 1 ? items.filter((i) => i.level === openLevel && !done.has(i.id) && !startedMu.has(i.miniUnitId) && !ex.has(i.id) && isOpen(i)) : [];
  const last = items.find((i) => i.id === completed[completed.length - 1]);
  const score = (i: PlaygroundItem) => {
    const cat = drawings.get(i.drawingId)?.category ?? '';
    let s = fav.has(cat) ? 3 : 0;
    if (last) s += Math.min(2, i.tags.filter((t) => last.tags.includes(t)).length);
    // A gentle stretch: the child's level and the one below rank above the very first steps.
    s += i.level === openLevel ? 1.5 : i.level === openLevel - 1 ? 1 : 0;
    return s;
  };
  const forYou = items
    .filter((i) => !done.has(i.id) && !ex.has(i.id) && i.level <= openLevel && isOpen(i))
    .map((i, k) => ({ i, k, s: score(i) }))
    .sort((a, b) => b.s - a.s || a.k - b.k)
    .map((x) => x.i);
  const triedCats = new Set(items.filter((i) => done.has(i.id)).map((i) => i.categoryId));
  const notTried: PlaygroundItem[] = [];
  const perCat = new Map<string, number>();
  for (const i of [...items].sort((a, b) => a.level - b.level)) {
    if (triedCats.has(i.categoryId) || done.has(i.id) || ex.has(i.id) || !isOpen(i)) continue;
    const n = perCat.get(i.categoryId) ?? 0;
    if (n >= 2) continue;
    perCat.set(i.categoryId, n + 1);
    notTried.push(i);
  }
  const openItems = items.filter(isOpen);
  const fresher = openItems.filter((i) => !done.has(i.id) && !ex.has(i.id));
  return { continueItems, fresh, forYou, notTried, surprise: fresher.length ? fresher : openItems };
}

/** Deterministic-friendly random pick (pass Math.random in the app; a seeded function in tests). */
export function pickSurprise(pool: PlaygroundItem[], rnd: () => number, avoid?: string): PlaygroundItem | undefined {
  const list = pool.length > 1 && avoid ? pool.filter((i) => i.id !== avoid) : pool;
  return list[Math.min(list.length - 1, Math.floor(rnd() * list.length))];
}
